import React, { useState, useEffect, useRef } from "react";
import { Formik, Field, Form as FormikForm, FormikHelpers, FormikProps } from 'formik';
import "./BookingForm.scss";
import { Form, Button, Row, Spinner } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import * as Yup from 'yup';
import Calendar from 'react-calendar';
import './Calendar.css';
import { ApiService } from "../../services/ApiService";
import moment from 'moment';
import { BsInfoCircle } from "react-icons/bs";
import ErrorModal from "../modals/ErrorModal";
import DuplicateModal from "../modals/DuplicateModal";
import { UtilitiesService } from "../../services/UtilitiesService";

const _ = require("lodash");

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

interface IObjectKeys {
    [key: string]: string | number;
}

interface Location {
    id: number;
    label: string;
    provAbbr: string;
    urlName: string;
    lat: string;
    lng: string;
}

interface Fields {
    type: number;
    location: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    birthMonth: number;
    birthDay: number;
    birthYear: number;
    reminder: boolean;
    onBehalf: boolean;
    guardianFirst: string;
    guardianLast: string;
    additionalInfo: string;
    time: string;
    date: string;
    isNewCustomer?: boolean;
}

interface DuplicateAppointment {
    ID: number;
    Location: string;
    Start: string,
    PatientID: number
}

interface UserLocation {
    lat: number;
    lng: number;
}


// RegEx for phone number validation
const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("*First Name is required"),
    lastName: Yup.string().required("*Last Name is required"),
    email: Yup.string().email("*Must be a valid email address").required("*Email is required"),
    phone: Yup.string().matches(phoneRegExp, "*Phone number is not valid").required("*Phone number required"),
    time: Yup.string().required("*Please select an appointment time"),
    location: Yup.number()
        .typeError('You must select a location')
        .required('Please select a location')
        .moreThan(0, 'Please select a location'),
});

const BookingForm = ({ title, type, acknowledgements, initialLocationSlug, askNewOrReturning }: { title: string, type: string, acknowledgements: string[], initialLocationSlug?: string, askNewOrReturning?: boolean }) => {

    const formikRef = useRef<FormikProps<Fields>>(null);
    const [typeID, setTypeID] = useState(0);
    const [apptType, setApptType] = useState('');
    const [locations, setLocations] = useState<{ [key: string]: any[] }>({});
    const [indexedLocations, setIndexedLocations] = useState<{ [key: string]: Location }>({});
    const [selectedLocation, setSelectedLocation] = useState(0);
    const [selectedLocationInfo, setSelectedLocationInfo] = useState<Location | null>(null)
    const [disabledDates, setDisabledDates] = useState<number[]>([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableTimes, setAvailableTimes] = useState([]);
    const [isLoadingTimes, setIsLoadingTimes] = useState(false);
    const [minDate, setMinDate] = useState(new Date());
    const [maxDate, setMaxDate] = useState(new Date());
    const [apiService, setApiService] = useState<ApiService | null>(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [duplicateAppt, setDuplicateAppt] = useState<DuplicateAppointment | null>(null);
    const [currentAppt, setCurrentAppt] = useState('');
    const [formData, setFormData] = useState<Fields | null>(null);
    const [minBirthYear, setMinBirthYear] = useState(0);
    const [buttonPressed, setButtonPressed] = useState(false);
    const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
    const [isNewCustomer, setIsNewCustomer] = useState<boolean | undefined>(undefined);
    const [typeUnavailable, setTypeUnavailable] = useState(false);

    const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>, setFieldValue: any): void => {

        const locationID = parseInt(e.target.value);
        setLocation(locationID, setFieldValue);
    }

    /**
     * Records the new-vs-returning-customer answer and refreshes availability, since it can
     * change which slots are shown at locations that restrict new customers to pedorthists.
     */
    const handleNewOrReturningChange = (value: boolean, setFieldValue: any): void => {
        setIsNewCustomer(value);
        setFieldValue("isNewCustomer", value);

        if (selectedLocation) {
            fetchDates(selectedLocation, value);
        }
    }

    const fetchAppointmentTypes = () => {

        const uri = "/api/booking/get-available-types";
        apiService?.get(uri)
            .then(async response => {

                const string = await response.text();

                var typeID: number = 0;
                var matched = false;

                if (string !== "") {
                    const json = JSON.parse(string);
                    if (Array.isArray(json)) {
                        json.forEach((element: { id: number, type: string; }) => {
                            if (element.type === type) {
                                typeID = element.id;
                                matched = true;

                                setTypeID(typeID);
                                setApptType(element.type.toLowerCase());
                            }
                        });
                    }

                    if (matched) {
                        fetchLocations(typeID);
                    } else {
                        // Nothing in the API response matched this page's appointment type (e.g. it
                        // was deactivated, renamed, or isn't configured in this environment yet) -
                        // fetchLocations(0) would otherwise 400 and crash on an unhandled response shape.
                        setTypeUnavailable(true);
                    }
                }
            });

    };

    const fetchLocations = (typeID: number) => {
        const uri = "/api/booking/get-available-locations/" + typeID;

        apiService?.get(uri)
            .then(async response => {
                const string = await response.text();

                if (string !== "") {
                    const json = JSON.parse(string);

                    if (Array.isArray(json)) {
                        let reformatted: any = {};
                        let indexedLocations: any = {};

                        json.forEach((element: {
                            id: number, label: string, province: string, provAbbr: string, urlName: string, lat: string, lng: string
                        }) => {
                            if (!reformatted[element.province]) {
                                reformatted[element.province] = [];
                            }
                            reformatted[element.province].push({
                                id: element.id,
                                label: element.label
                            });

                            indexedLocations[element.id] = {
                                id: element.id,
                                label: element.label,
                                provAbbr: element.provAbbr.toLocaleLowerCase(),
                                urlName: element.urlName,
                                lat: element.lat,
                                lng: element.lng,
                            }

                        })

                        setLocations(reformatted);
                        setIndexedLocations(indexedLocations);
                    }
                }

            });
    };

    /**
     * Appends the tri-state new-vs-returning-customer flag as a query param when it's been
     * answered. Left off entirely (unset) if the user hasn't answered - this keeps the API
     * call backwards-compatible/opt-in.
     *
     * Takes the flag explicitly rather than reading the `isNewCustomer` state directly, since
     * callers that just changed the answer (setIsNewCustomer) would otherwise close over the
     * pre-update value - state updates aren't visible until the next render.
     */
    const appendIsNewCustomerParam = (uri: string, isNewCustomerValue: boolean | undefined) => {
        if (isNewCustomerValue === undefined) {
            return uri;
        }

        return uri + "?isNewCustomer=" + (isNewCustomerValue ? "1" : "0");
    }

    const fetchDates = (locationID: number, isNewCustomerValue: boolean | undefined = isNewCustomer) => {

        const uri = appendIsNewCustomerParam("/api/booking/get-available-dates/" + typeID + "/" + locationID, isNewCustomerValue);

        apiService?.get(uri)
            .then(async response => {
                const string = await response.text();

                if (string !== "") {
                    const response = JSON.parse(string);

                    var i: number;
                    var disabledDates: number[] = [];

                    for (i = 0; i < response.disabledDates.length; i++) {
                        const date = convertStringToDate(response.disabledDates[i].date).getTime();
                        disabledDates.push(date);
                    }

                    setDisabledDates(disabledDates);
                    setMinDate(convertStringToDate(response.minDate.date));
                    setMaxDate(convertStringToDate(response.maxDate.date));
                    setSelectedDate(convertStringToDate(response.defaultDate.date));

                    fetchTimes(response.defaultDate.date, locationID, isNewCustomerValue);

                }
            });
    }

    const fetchTimes = (date: string, location: number, isNewCustomerValue: boolean | undefined = isNewCustomer) => {

        const uri = appendIsNewCustomerParam("/api/booking/get-available-times/" + typeID + "/" + location + "/" + date, isNewCustomerValue);

        setAvailableTimes([]);
        setIsLoadingTimes(true);
        apiService?.get(uri)
            .then(async response => {
                const string = await response.text();

                if (string !== "") {
                    const times = JSON.parse(string);
                    setAvailableTimes(times);
                }
            })
            .finally(() => {
                setIsLoadingTimes(false);
            });
    }

    const handleCalendarChange = (value: Value, setFieldValue: any) => {

        if (value) {
            const formattedDate = getFormattedDate(value);
            const newDay = convertStringToDate(formattedDate);

            fetchTimes(formattedDate, selectedLocation)
            setFieldValue("date", formattedDate);
            setSelectedDate(newDay);

        }

    }

    const getFormattedDate = (date: any) => {
        return moment(date).format('YYYY-MM-DD');
    }

    const convertStringToDate = (dateString: string) => {
        return moment(dateString, 'YYYY-MM-DD').toDate();
    }



    const handleCheckboxChange = () => {
        let checked = true;
        let checkboxes = document.querySelectorAll<HTMLInputElement>('#acknowledgements input[type=checkbox]');

        checkboxes.forEach(function (input) {
            if (!input.checked) {
                checked = false;
            }
        })

        const submitBtn = document.querySelector('button[type=submit]');

        if (submitBtn) {
            submitBtn.classList.toggle('acknowledgements-complete', checked);
        }
    }

    const renderOptions = (options: any) => {
        return options.map((option: Location) => {
            return (
                <option key={option.id} value={option.id}>
                    {option.label}
                </option>
            );
        });

    };

    const closeModal = () => {
        setIsErrorModalOpen(false);
        setIsDuplicateModalOpen(false);
        setButtonPressed(false);
    };

    const moveAppt = () => {
        setButtonPressed(true);

        let data = _.clone(formData);
        data.moveDuplicateAppointment = duplicateAppt?.ID;

        const uri = "/api/booking/confirm";

        apiService?.post(uri, data)
            .then(async response => {
                const string = await response.text();
                const resp = string === "" ? {} : JSON.parse(string);

                if (resp.hasOwnProperty('Success')) {
                    handleSuccessResponse(resp);
                    apiService.clearToken();
                }

            })
    }

    const handleSuccessResponse = (resp: any) => {
        if (resp.Success) {

            const province = selectedLocationInfo?.provAbbr;
            const urlName = selectedLocationInfo?.urlName;

            const remoteBookingID = resp.RemoteBookingID;
            const localBookingID = resp.LocalBookingID;
            const path = '/confirmation/' + apptType + '/' + province + '/' + urlName;

            window.sessionStorage.setItem('bookingConfirmed', '1');
            window.sessionStorage.setItem('remoteBookingID', remoteBookingID);
            window.sessionStorage.setItem('localBookingID', localBookingID);

            const href = UtilitiesService.appendUTMParamsToHref(path);
            window.location.href = href;

        } else {
            setIsErrorModalOpen(true);
        }
    }

    /**
     * Sets location react states and form value. Also, fetches the dates for the location
     * 
     * @param locationID 
     * @param setFieldValue 
     */
    const setLocation = (locationID: number, setFieldValue: any) => {
        setSelectedLocation(locationID);
        const locationData = indexedLocations[locationID];
        setSelectedLocationInfo(locationData);

        if (locationID) {
            fetchDates(locationID);
        }

        setFieldValue("location", locationID);
    }

    /**
     * Returns the distance in km between 2 coordinates
     * 
     * @param lat1 
     * @param lng1 
     * @param lat2 
     * @param lng2 
     * @returns 
     */
    function getDistance(lat1: number, lng1: number, lat2: number, lng2: number) {
        const R = 6371; // Radius of Earth in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in km
    }


    /**
     * Returns the kintec location closest to the user's current position
     * 
     * @param userLat 
     * @param userLng 
     * @returns 
     */
    const findClosestLocation = (userLat: number, userLng: number) => {
        const locations = Object.values(indexedLocations);

        const closest = locations.reduce((prev: Location, curr: Location) => {
            const prevDist = getDistance(userLat, userLng, parseFloat(prev.lat), parseFloat(prev.lng));
            const currDist = getDistance(userLat, userLng, parseFloat(curr.lat), parseFloat(curr.lng));
            return (currDist < prevDist) ? curr : prev;
        });

        return closest;
    }

    /**
     * Fetchs the location based on the user's current IP address
     * 
     * @param setFieldValue 
     */
    const fetchLocationByIP = async () => {
        const uri = "/api/booking/get-my-location";

        apiService?.get(uri)
            .then(async response => {
                const string = await response.text();
                const resp = string === "" ? {} : JSON.parse(string);

                setUserLocation({lat: resp.lat, lng: resp.lng});
            })
    }

    /**
     * Sets the location dropdown based on the user's current IP address
     * 
     * @param setFieldValue 
     */
    const setLocationByIP = (setFieldValue: any) => {
        if (userLocation?.lat && userLocation?.lng) {
            const closest = findClosestLocation(userLocation?.lat, userLocation?.lng);

            const locationID = closest.id;
            setLocation(locationID, setFieldValue);
        }
    }

    /**
     * Initializes selected location dropdown list using the user's current geolocation 
     * or their IP address
     * 
     * @param setFieldValue 
     */
    const initLocation = (setFieldValue: any) => {
        // First, try browser geolocation
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;

                    const closest = findClosestLocation(latitude, longitude);
                    const locationID = closest.id;

                    setLocation(locationID, setFieldValue);
                },
                () => {
                    // If denied or error, fallback to IP
                    setLocationByIP(setFieldValue);
                }
            )
        } else {
            setLocationByIP(setFieldValue);
        }
    }

    const init = () => {
        const currentYear = new Date().getFullYear();
        const minYear = currentYear - 95;
        setMinBirthYear(minYear);
    }

    useEffect(() => {

        // remove token that was already used to book an appointment
        if (window.sessionStorage.getItem('bookingConfirmed')) {
            window.sessionStorage.clear();
        }

        const initialize = async () => {
            init();
            return ApiService.create();
        }

        if (apiService) {
            fetchAppointmentTypes();
        } else {

            initialize().then(service => {
                setApiService(service);
            });

        }

    }, [apiService]);

    // Pre-select a location from the campaign landing page's ?location=<urlName> query param
    useEffect(() => {
        if (!initialLocationSlug || selectedLocation) {
            return;
        }

        const match = Object.values(indexedLocations).find((location) => location.urlName === initialLocationSlug);

        if (match && formikRef.current) {
            setLocation(match.id, formikRef.current.setFieldValue);
        }
    }, [indexedLocations, initialLocationSlug, selectedLocation]);

    return (

        <div>
            <h4>{title} Appointment Booking</h4>
            {typeUnavailable ? (
                <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
                    <BsInfoCircle style={{ flexShrink: 0 }} />
                    <span>Online booking for {title} isn't available right now. Please contact us directly to book this appointment.</span>
                </div>
            ) : (
            <Formik
                innerRef={formikRef}
                initialValues={{
                    type: typeID,
                    location: selectedLocation,
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    birthMonth: 1,
                    birthDay: 1,
                    birthYear: minBirthYear,
                    reminder: true,
                    onBehalf: false,
                    guardianFirst: '',
                    guardianLast: '',
                    additionalInfo: '',
                    time: '',
                    date: ''

                }}
                validationSchema={validationSchema}
                onSubmit={(
                    values: Fields,
                    { setSubmitting, resetForm }: FormikHelpers<Fields>

                ) => {
                    setButtonPressed(true);

                    const birthMonth = String(values.birthMonth).padStart(2, '0');
                    const birthDay = String(values.birthDay).padStart(2, '0');
                    const birthYear = values.birthYear ? values.birthYear : minBirthYear;

                    let data = _.clone(values);
                    data.type = typeID;
                    data.birthdate = birthYear + '-' + birthMonth + '-' + birthDay;
                    data.date = getFormattedDate(selectedDate);
                    data.acknowledge = 1;

                    const uri = "/api/booking/confirm";

                    apiService?.post(uri, data)
                        .then(async response => {
                            const string = await response.text();
                            const resp = string === "" ? {} : JSON.parse(string);

                            if (resp.hasOwnProperty('Success')) {
                                handleSuccessResponse(resp);
                                apiService.clearToken();
                            }

                            if (resp.hasOwnProperty('Duplicate') && resp.Duplicate) {
                                setIsDuplicateModalOpen(true);
                                setButtonPressed(false);
                                setDuplicateAppt(resp.Appointment);

                                const currentAppt = data.date + ' ' + data.time;

                                setCurrentAppt(currentAppt);
                                setFormData(data);
                            }

                            setSubmitting(false);
                            console.log(resp);
                            return resp;
                        });


                }}
            >
                {({ values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    isSubmitting }) => {
                    return (

                        <FormikForm>

                            <Field type="hidden" value={typeID} name="type" />

                            {title !== 'Kintec Anywhere' && (
                                <Row className="mb-3">
                                    <Form.Group as={Col} controlId="formLocation">
                                        <Form.Label>Location</Form.Label>
                                        <Form.Select
                                            name="location"
                                            value={values.location}
                                            className={touched.location && errors.location ? "error" : undefined}
                                            onChange={(e) => { handleLocationChange(e, setFieldValue) }}>
                                            {(() => {
                                                let groupedOptions: any[] = [];

                                                groupedOptions = Object.keys(locations).map((group: string, index: number) => {
                                                    return (
                                                        <optgroup key={index + 1} label={group}>
                                                            {renderOptions(locations[group])}
                                                        </optgroup>
                                                    );
                                                });

                                                groupedOptions.unshift(<option key='0' value='0'>(Select one)</option>);

                                                return groupedOptions;
                                            })()}

                                        </Form.Select>
                                        {touched.location && errors.location ? (
                                            <div className="error-message">{errors.location}</div>
                                        ) : null}

                                    </Form.Group>
                                </Row>
                            )}

                            {askNewOrReturning && (
                                <Row className="mb-3">
                                    <Form.Group as={Col} controlId="formIsNewCustomer">
                                        <Form.Label>Have you visited us for footwear before?</Form.Label>
                                        <div>
                                            <Form.Check
                                                inline
                                                type="radio"
                                                id="isNewCustomer-new"
                                                name="isNewCustomerChoice"
                                                label="No, I'm a new customer"
                                                checked={values.isNewCustomer === true}
                                                onChange={() => { handleNewOrReturningChange(true, setFieldValue) }}
                                            />
                                            <Form.Check
                                                inline
                                                type="radio"
                                                id="isNewCustomer-returning"
                                                name="isNewCustomerChoice"
                                                label="Yes, I'm a returning customer"
                                                checked={values.isNewCustomer === false}
                                                onChange={() => { handleNewOrReturningChange(false, setFieldValue) }}
                                            />
                                        </div>
                                    </Form.Group>
                                </Row>
                            )}

                            {(title !== 'Kintec Anywhere' && selectedLocation === 0) ? (
                                <div className="alert alert-info d-flex align-items-center gap-2" role="alert">
                                    <BsInfoCircle style={{ flexShrink: 0 }} />
                                    <span>Please select a location before proceeding.</span>
                                </div>
                            ) : (
                            <>

                            <Row className="mb-3">
                                <Form.Label as={Col} sm={12}>Date & Time</Form.Label>
                                <Form.Group as={Col} sm={12} md={6} controlId="dateTime">
                                    <Calendar prev2Label={null} next2Label={null} calendarType="gregory" value={selectedDate} minDate={minDate} maxDate={maxDate} onChange={val => { handleCalendarChange(val, setFieldValue) }} tileDisabled={({ date }) => { return disabledDates.includes(date.getTime()) }} />
                                </Form.Group>
                                <Form.Group as={Col} sm={12} md={6}>

                                    {(() => {
                                        if (isLoadingTimes) {
                                            return (<div className="d-flex justify-content-center align-items-center h-100">
                                                <Spinner animation="border" size="sm" role="status" aria-label="Loading available times" />
                                                </div>);
                                        }

                                        if (availableTimes.length > 0) {
                                            let options: any = [];

                                            _.forEach((availableTimes), function (time: any) {
                                                options.push(<Col sm={12} md={6}><Form.Check
                                                    type='radio'
                                                    name='time'
                                                    key={`time_${time.time}`}
                                                    id={`time_${time.time}`}
                                                    label={`${time.label}`}
                                                    value={`${time.time}`}
                                                    className="time"
                                                    onChange={handleChange}
                                                /></Col>)
                                            })

                                            return (<Row id="date-options">{options}</Row>)
                                        }

                                        return <p>Sorry no times available on that date.</p>;
                                    })()}

                                </Form.Group>
                                {errors.time ? (
                                    <Col sm={12} className="error-message">{errors.time}</Col>
                                ) : null}
                            </Row>


                            <h4>Patient Information</h4>

                            <Row className="mb-3">
                                <Form.Group as={Col} sm={12} md={6} controlId="formFirstName">
                                    <Form.Label>Legal First Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        value={values.firstName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={touched.firstName && errors.firstName ? "error" : undefined}
                                    />
                                    {touched.firstName && errors.firstName ? (
                                        <div className="error-message">{errors.firstName}</div>
                                    ) : null}
                                </Form.Group>
                                <Form.Group as={Col} sm={12} md={6} controlId="formLastName">
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        value={values.lastName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={touched.lastName && errors.lastName ? "error" : undefined}
                                    />
                                    {touched.lastName && errors.lastName ? (
                                        <div className="error-message">{errors.lastName}</div>
                                    ) : null}
                                </Form.Group>
                                <Form.Group as={Col} sm={12} controlId="formEmail">
                                    <Form.Label>Email Address</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={touched.email && errors.email ? "error" : undefined}
                                    />
                                    {touched.email && errors.email ? (
                                        <div className="error-message">{errors.email}</div>
                                    ) : null}
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Col sm={12} md={5}>
                                    <Form.Label>Birthday</Form.Label>
                                    <Row>
                                        <Col id="birthday-fields">
                                            <Form.Select
                                                name="birthMonth"
                                                value={values.birthMonth}
                                                onChange={handleChange}
                                            >
                                                <option value="1">January</option>
                                                <option value="2">February</option>
                                                <option value="3">March</option>
                                                <option value="4">April</option>
                                                <option value="5">May</option>
                                                <option value="6">June</option>
                                                <option value="7">July</option>
                                                <option value="8">August</option>
                                                <option value="9">September</option>
                                                <option value="10">October</option>
                                                <option value="11">November</option>
                                                <option value="12">December</option>
                                            </Form.Select>

                                            <Form.Select
                                                name="birthDay"
                                                value={values.birthDay}
                                                onChange={handleChange}
                                            >
                                                {(() => {
                                                    let options = [];
                                                    for (let i = 1; i <= 31; i++) {
                                                        options.push(<option key={i}>{i}</option>);
                                                    }
                                                    return options;
                                                })()}

                                            </Form.Select>

                                            <Form.Select
                                                name="birthYear"
                                                value={values.birthYear}
                                                onChange={handleChange}
                                            >
                                                {(() => {
                                                    let options = [];
                                                    const currentYear = new Date().getFullYear();

                                                    for (let i = minBirthYear; i <= currentYear; i++) {
                                                        options.push(<option key={i}>{i}</option>);
                                                    }
                                                    return options;
                                                })()}
                                            </Form.Select>
                                        </Col>
                                    </Row>
                                </Col>

                                <Form.Group as={Col} sm={12} md={3} controlId="formPhone">
                                    <Form.Label>Contact Number</Form.Label>

                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={values.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={touched.phone && errors.phone ? "error" : undefined}
                                    />
                                    {touched.phone && errors.phone ? (
                                        <div className="error-message">{errors.phone}</div>
                                    ) : null}
                                </Form.Group>
                                <Form.Group as={Col} sm={12} md={4} controlId="formReminder" className="reminder-container">
                                    <Form.Check
                                        type="checkbox"
                                        name="reminder"
                                        checked={values.reminder}
                                        label="Receive reminder as text message"
                                        className="reminder"
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <div className="col">
                                    <div className="guardian-container">
                                        <Form.Group controlId="formOnBehalf">

                                            <Form.Check
                                                type="checkbox"
                                                name="onBehalf"
                                                checked={values.onBehalf}
                                                label="Booking on behalf of a dependent"
                                                onChange={handleChange}
                                            />
                                        </Form.Group>

                                        <Row>
                                            <Form.Group as={Col} sm={12} md={6} controlId="formGuardianFirst">
                                                <Form.Label>Guardian's First Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="guardianFirst"
                                                    value={values.guardianFirst}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </Form.Group>
                                            <Form.Group as={Col} sm={12} md={6} controlId="formGuardianLast">
                                                <Form.Label>Guardian's Last Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="guardianLast"
                                                    value={values.guardianLast}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                            </Form.Group>
                                        </Row>

                                    </div>
                                </div>
                            </Row>
                            <Row>
                                <Form.Group controlId="formAdditionalInfo">
                                    <Form.Label>Anything else we should know? Additional comments or questions <span className="note">(Optional)</span></Form.Label>
                                    <Form.Control as="textarea" name="additionalInfo" rows={3} value={values.additionalInfo} onChange={handleChange} onBlur={handleBlur} />
                                </Form.Group>
                            </Row>
                            <Row id="acknowledgements">
                                <Form.Group controlId="acknowledge">
                                    {acknowledgements.map((acknowledge, i) => {
                                        let key = "acknowledge_" + i

                                        return (<Form.Check
                                            type="checkbox"
                                            label={<span dangerouslySetInnerHTML={{ __html: acknowledge }} />}
                                            key={key}
                                            id={key}
                                            onChange={handleCheckboxChange}
                                            required
                                        />)
                                    })}
                                </Form.Group>
                            </Row>
                            <Row>
                                <div className="col">
                                    <Button type="submit" className="btn btn-default" disabled={buttonPressed}>Confirm Appointment</Button>
                                </div>
                            </Row>

                            </>
                            )}
                        </FormikForm>)
                }
            }

            </Formik>
            )}
            <ErrorModal isOpen={isErrorModalOpen} closeModal={closeModal} />

            {duplicateAppt &&
                <DuplicateModal isOpen={isDuplicateModalOpen} duplicateAppt={duplicateAppt.Start} currentAppt={currentAppt} apptType={apptType} location={selectedLocationInfo} moveAppt={moveAppt} closeModal={closeModal} buttonPressed={buttonPressed} />}


        </div>
    );
};

export default BookingForm;